import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import PortalLayout from "@/components/ventrix/PortalLayout";
import { api, downloadAuthedBlob } from "@/lib/api";
import { formatMoney, Kpi } from "@/components/ventrix/atoms";
import { Package, AlertOctagon, CheckCircle2, DollarSign, TrendingUp, Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";

const COLORS = ["#0E2F76", "#d41f26", "#A9C0E0", "#1D4ED8", "#B45309", "#166534"];

export default function Reports() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/v1/reports/summary").then(({data})=>setD(data)); }, []);

  if (!d) return <PortalLayout section="admin"><div className="vtx-skeleton h-24" /></PortalLayout>;

  const byMode = {};
  (d.by_mode_status || []).forEach(r => { byMode[r.mode] = (byMode[r.mode] || 0) + r.count; });
  const modeData = Object.entries(byMode).map(([mode, count]) => ({ mode: (mode||"other").toUpperCase(), count }));
  const statusData = {};
  (d.by_mode_status || []).forEach(r => { statusData[r.status] = (statusData[r.status] || 0) + r.count; });
  const statusChart = Object.entries(statusData).map(([status, count]) => ({ status: status.replace(/_/g," "), count }));

  return (
    <PortalLayout section="admin">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="vtx-eyebrow">/ Reports</div>
          <h1 className="mt-2 text-3xl font-black text-[#0E2F76] tracking-tighter">Operations reports</h1>
        </div>
        <button type="button"
          onClick={() => downloadAuthedBlob("/v1/reports/shipments.csv", "shipments.csv").catch(() => toast.error("Could not export the CSV."))}
          className="vtx-btn-primary inline-flex items-center gap-2" data-testid="download-csv"><Download size={14}/> Export shipments CSV</button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Total shipments" value={d.total_shipments} Icon={Package} />
        <Kpi label="Delivered" value={d.delivered} Icon={CheckCircle2} accent="#166534" />
        <Kpi label="Exceptions" value={d.exceptions} Icon={AlertOctagon} accent="#d41f26" />
        <Kpi label="On-time %" value={`${d.on_time_pct}%`} Icon={TrendingUp} accent="#1D4ED8" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="vtx-card p-5 lg:col-span-2">
          <div className="vtx-eyebrow">Shipments by status</div>
          <div className="h-72 mt-3 w-full" style={{minWidth: 0, minHeight: 240}}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={240}>
              <BarChart data={statusChart}>
                <XAxis dataKey="status" tick={{fontSize: 10, fill: "#475569"}} />
                <YAxis tick={{fontSize: 10, fill: "#475569"}} />
                <Tooltip contentStyle={{background: "#fff", border: "1px solid #A9C0E0", borderRadius: 2, fontSize: 12}}/>
                <Bar dataKey="count" fill="#0E2F76" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="vtx-card p-5">
          <div className="vtx-eyebrow">By mode</div>
          <div className="h-72 mt-3 w-full" style={{minWidth: 0, minHeight: 240}}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={240}>
              <PieChart>
                <Pie data={modeData} dataKey="count" nameKey="mode" cx="50%" cy="50%" outerRadius={80} label>
                  {modeData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Legend wrapperStyle={{fontSize: 11}}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <div className="vtx-card p-6 bg-[#0E2F76] text-white">
          <div className="vtx-eyebrow text-[#A9C0E0]">Revenue paid</div>
          <div className="mt-2 text-4xl font-black tracking-tighter">{formatMoney(d.revenue_paid)}</div>
        </div>
        <div className="vtx-card p-6">
          <div className="vtx-eyebrow">Outstanding</div>
          <div className="mt-2 text-4xl font-black tracking-tighter text-[#d41f26]">{formatMoney(d.revenue_outstanding)}</div>
        </div>
      </div>

      {d.rated_shipments > 0 && (
        <div className="vtx-card p-6 mt-6" data-testid="routing-pnl">
          <div className="vtx-eyebrow flex items-center gap-2"><DollarSign size={14} /> Routing P&amp;L · {d.rated_shipments} rated shipment{d.rated_shipments === 1 ? "" : "s"}</div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <Kpi label="Quoted total" value={formatMoney(d.quoted_total)} Icon={TrendingUp} />
            <Kpi label="Routing cost" value={formatMoney(d.routing_cost_total)} Icon={Package} accent="#B45309" />
            <Kpi label="Est. margin" value={formatMoney(d.estimated_margin)} Icon={DollarSign} accent={d.estimated_margin >= 0 ? "#166534" : "#d41f26"} />
            <Kpi label="Margin %" value={d.estimated_margin_pct != null ? `${d.estimated_margin_pct}%` : "—"} Icon={TrendingUp} accent={d.estimated_margin >= 0 ? "#166534" : "#d41f26"} />
          </div>
        </div>
      )}
    </PortalLayout>
  );
}
